import { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { getAssetLink } from '../../../helpers/get-asset-link'
import ResendActivationLink from '../ResendActivationLink'
import {
  createManager,
  selectManagerById,
  updateManager,
} from '../../../reducers/Organisation/organisationManagersReducer'
import { formValidation } from '../../FormLegacy/validation'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const OrganisationManagerForm = ({ manager, params, createManager, updateManager }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [formError, setFormError] = useState('')
  const { displaySnackbar } = useSnackbar()

  const isCreateNewMode = () => params.managerId && params.managerId === 'new'

  const handleFormSubmit = async (
    { id, avatar, firstName, lastName, email },
    { setSubmitting },
  ) => {
    const isNew = isCreateNewMode()
    const newManagerData = {
      firstName: firstName,
      lastName: lastName,
      email: email,
    }

    if (avatar.file) {
      newManagerData.avatar = avatar.file
    }

    setFormError('')

    try {
      setSubmitting(true)
      if (isNew) {
        await createManager(newManagerData)
        setSubmitting(false)
        displaySnackbar(t('organisation:managers.form.toasts.created'))
      } else {
        await updateManager(newManagerData, id)
        setSubmitting(false)
        displaySnackbar(t('organisation:managers.form.toasts.updated'))
      }
    } catch {
      setSubmitting(false)
      setFormError(t('organisation:shared.serverError'))
    }
  }

  const validationSchema = Yup.object().shape({
    firstName: formValidation.required,
    lastName: formValidation.required,
    email: formValidation.email,
  })

  return manager ? (
    <Formik
      enableReinitialize={true}
      initialValues={{
        id: manager.id || '',
        avatar: {
          file: false,
          image: getAssetLink({ path: 'chitown-avatar/avatar', file: manager?.user?.avatar }),
        },
        firstName: manager.user.firstName || '',
        lastName: manager.user.lastName || '',
        email: manager.user.email || '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        errors,
        touched,
        setFieldTouched,
        isSubmitting,
        dirty,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="ctp-manager">
            <div className="ctp-manager__header">
              <h2>
                {isCreateNewMode()
                  ? t('organisation:managers.form.titleNew')
                  : t('organisation:managers.form.titleEdit')}
              </h2>
            </div>
            <div className="form form--object-profile">
              <div className="column">
                <div className="columns">
                  <div className="column">
                    <ImageField
                      name={'avatar'}
                      label={t('organisation:shared.fields.avatar.label')}
                      placeholderImage={getAssetLink({
                        path: 'chitown-avatar/avatar',
                        file: 'avatar.png',
                      })}
                      imageRatio={1}
                      value={values.avatar}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="column">
                    {!isCreateNewMode() && values.email && (
                      <ResendActivationLink
                        email={values.email}
                        onError={() => {
                          setFormError(t('organisation:shared.serverError'))
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="columns">
                  <div className="column">
                    <TextField
                      name={'firstName'}
                      label={t('organisation:shared.fields.firstName.label')}
                      placeholder={t('organisation:shared.fields.firstName.placeholder')}
                      value={values.firstName}
                      onChange={handleChange}
                      isError={touched.firstName && !!errors.firstName}
                      onBlur={() => setFieldTouched('firstName')}
                    />
                  </div>
                  <div className="column">
                    <TextField
                      name={'lastName'}
                      label={t('organisation:shared.fields.lastName.label')}
                      placeholder={t('organisation:shared.fields.lastName.placeholder')}
                      value={values.lastName}
                      onChange={handleChange}
                      isError={touched.lastName && !!errors.lastName}
                      onBlur={() => setFieldTouched('lastName')}
                    />
                  </div>
                </div>
                <TextField
                  name={'email'}
                  label={t('organisation:shared.fields.email.label')}
                  placeholder={t('organisation:shared.fields.email.placeholder')}
                  value={values.email}
                  onChange={handleChange}
                  isError={touched.email && !!errors.email}
                  errorLabel={touched.email && errors.email}
                  onBlur={() => setFieldTouched('email')}
                  disabled={!isCreateNewMode()}
                />
              </div>
              <div className="columns form__actions">
                <div className="column">
                  <button
                    type={'submit'}
                    className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                    disabled={!dirty || isSubmitting}
                  >
                    {t('common:actions.save')}
                  </button>
                  <label className="help is-danger">{formError}</label>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  ) : (
    <p className="has-text-centered">{t('organisation:managers.form.notFound')}</p>
  )
}

OrganisationManagerForm.propTypes = {
  params: PropTypes.object,
  manager: PropTypes.object.isRequired,
  createManager: PropTypes.func.isRequired,
  updateManager: PropTypes.func.isRequired,
}

export default connect(
  (state, { params }) => ({
    manager: selectManagerById(state)(params.managerId),
  }),
  { createManager, updateManager },
)(OrganisationManagerForm)
